import React, { useId, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { HousePanel, HouseStatCard, type KitIcon } from './trackerKit';
import { formatCompactCurrency } from '../../../utils/formatters';

/**
 * The Pipeline tab's visual vocabulary, taken from the mockup Evoke were shown
 * (valutten-linkedin/demos/christian-paterson-evoke/evoke-pipeline-tracker-mockup.html).
 *
 * NOTHING HERE HOLDS DATA. Every component takes what the report computed from
 * the committed BigQuery snapshot. There is no sample data, no placeholder
 * number and no fallback figure anywhere in this file: if a value is missing it
 * renders as absent rather than as a plausible-looking invention.
 */

export const SURFACE = 'rounded-xl border border-gray-700/70 bg-gray-800/40';

/** The mockup's palette, in funnel order. Cycled if a tenant has more stages. */
export const STAGE_COLOURS = [
  '#3b82f6', '#0891b2', '#7c3aed', '#f59e0b', '#2563eb',
  '#a78bfa', '#16a34a', '#0ea5e9', '#22c55e', '#eab308',
];

export const stageColour = (index: number): string =>
  STAGE_COLOURS[index % STAGE_COLOURS.length];

/** `$685M`. Cards are scanned, not audited; the exact figure lives in tables. */
export function money(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '-';
  return formatCompactCurrency(cents);
}

export const num = (n: number | null | undefined): string =>
  (n === null || n === undefined || !Number.isFinite(n) ? '-' : n.toLocaleString('en-AU'));

export type TileTone = 'brand' | 'green' | 'amber' | 'violet';

/**
 * Each tone's colours, precomputed rather than mixed in CSS so every browser and the test DOM read
 * the same values. `rgb` is the tone itself (the top bar, the tint and the glow); `border` and
 * `label` are the tone mixed into the surface grey and the label grey respectively.
 */
const TILE: Record<TileTone, { rgb: string; bar: string; border: string; label: string; value: string }> = {
  brand: { rgb: '37,99,235', bar: '#2563eb', border: '#304e8c', label: '#7b9eeb', value: '#60a5fa' },
  green: { rgb: '22,163,74', bar: '#16a34a', border: '#2a664e', label: '#73c292', value: '#34d399' },
  amber: { rgb: '245,158,11', bar: '#f59e0b', border: '#7f6436', label: '#eebf70', value: '#fbbf24' },
  violet: { rgb: '124,58,237', bar: '#7c3aed', border: '#513e8c', label: '#ab88ec', value: '#a78bfa' },
};

/**
 * A headline tile, in the house stat card look (trackerKit): the tone's accent bar, tint and icon
 * badge, an uppercase label, and the large value TINTED to the tone. The tint is what stops four
 * tiles reading as one grey block (founder-approved mockup, 5 Oct 2026). Props are unchanged; `icon`
 * is optional.
 */
export const Tile: React.FC<{
  label: string;
  value: string;
  footer?: React.ReactNode;
  tone?: TileTone;
  icon?: KitIcon;
  /** Full explanation, in an info tooltip beside the label. */
  info?: React.ReactNode;
}> = ({ label, value, footer, tone = 'brand', icon, info }) => {
  const c = TILE[tone];
  return (
    <HouseStatCard
      label={label}
      value={value}
      footer={footer}
      icon={icon}
      info={info}
      color={c.bar}
      valueColor={c.value}
      dataTone={tone}
      testId="tile"
    />
  );
};

/**
 * A panel, in the house look (HousePanel): brand blue accent unless `accent` is passed. Same
 * structure as before (a section, an h3 title, an optional badge and blurb).
 */
export const Panel: React.FC<{
  title: string;
  badge?: React.ReactNode;
  blurb?: React.ReactNode;
  /** Accent colour; brand blue by default. */
  accent?: string;
  icon?: KitIcon;
  actions?: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, badge, blurb, accent, icon, actions, collapsible, defaultOpen, children }) => (
  <HousePanel
    title={title}
    badge={badge}
    blurb={blurb}
    color={accent}
    icon={icon}
    actions={actions}
    collapsible={collapsible}
    defaultOpen={defaultOpen}
  >
    {children}
  </HousePanel>
);

/** One section that cannot be shown yet, because an input nobody has supplied is missing. */
export interface ComingSoonItem {
  /** The section's name, as it will read on the tab once it can be shown. */
  section: string;
  /** What is missing, in one sentence. */
  needs: string;
  /** Who owes it, e.g. "Matt Spears (Evoke)" or "Salestrekker". */
  from: string;
}

export const COMING_SOON_TITLE = 'Coming when the data is available';

/** "6 more sections will appear when their data arrives". */
export const comingSoonSummary = (count: number): string =>
  count === 1
    ? '1 more section will appear when its data arrives'
    : `${count} more sections will appear when their data arrives`;

/**
 * Every section on a tab whose figure cannot honestly be computed yet, in ONE compact panel at the
 * bottom of the tab, collapsed by default.
 *
 * Each blocked section used to be its own full-size card saying "Waiting on ...", and a tab of
 * those read as an empty product (founder, 1 Oct 2026). A blocked section still shows no number:
 * a placeholder figure would be read as a measurement. It is named here instead, with what it
 * needs and who owes it, and becomes a real section once the input arrives.
 *
 * A section that is only partly blocked keeps its live parts on the tab; only its waiting parts
 * are listed here. Renders nothing when nothing on the tab is waiting.
 */
export const ComingSoonList: React.FC<{ items: ReadonlyArray<ComingSoonItem> }> = ({ items }) => {
  const [open, setOpen] = useState(false);
  const listId = useId();
  if (items.length === 0) return null;
  return (
    <section className={`${SURFACE} px-5 py-3`} data-testid="coming-soon-list" aria-label={COMING_SOON_TITLE}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-left"
      >
        {open
          ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
          : <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />}
        <span className="text-[0.8125rem] font-semibold text-gray-300">{COMING_SOON_TITLE}</span>
        <span className="text-xs text-gray-500">{comingSoonSummary(items.length)}</span>
      </button>
      {open && (
        <ul id={listId} className="mt-3 divide-y divide-gray-700/50 border-t border-gray-700/50">
          {items.map((item) => (
            <li
              key={item.section}
              data-testid="coming-soon-item"
              className="grid gap-x-4 gap-y-0.5 py-2 text-xs sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_minmax(0,12rem)]"
            >
              <b className="font-semibold text-gray-200">{item.section}</b>
              <span className="text-gray-400">
                <span className="sr-only">Needs: </span>
                {item.needs}
              </span>
              <span className="text-gray-500 sm:text-right">From {item.from}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export const Pill: React.FC<{
  children: React.ReactNode;
  tone?: 'muted' | 'good' | 'warn' | 'bad' | 'brand';
}> = ({ children, tone = 'muted' }) => {
  const map = {
    muted: 'bg-white/[0.04] text-gray-400 border-gray-700',
    good: 'bg-green-500/15 text-green-300 border-green-500/30',
    warn: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    bad: 'bg-red-500/15 text-red-300 border-red-500/30',
    brand: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  } as const;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${map[tone]}`}>
      {children}
    </span>
  );
};

/** One funnel row: label, median age, counts, and a proportional bar. */
export const FunnelRow: React.FC<{
  label: string;
  medianAgeDays: number | null;
  deals: number;
  valueCents: number;
  widthPct: number;
  colour: string;
}> = ({ label, medianAgeDays, deals, valueCents, widthPct, colour }) => (
  <div>
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[0.8125rem]">
      <b className="font-semibold text-gray-200">{label}</b>
      <em className="mr-auto text-xs font-medium not-italic text-gray-400">
        {medianAgeDays === null ? 'no age recorded' : `median ${medianAgeDays}d in stage`}
      </em>
      <span className="whitespace-nowrap tabular-nums text-gray-400">
        {num(deals)} deals &middot; {money(valueCents)}
      </span>
    </div>
    <div className="mt-1.5 h-3 overflow-hidden rounded-full bg-gray-900/70">
      <div
        className="h-full rounded-full transition-[width] duration-500 ease-out"
        style={{ width: `${Math.max(3, widthPct)}%`, background: colour }}
      />
    </div>
  </div>
);

/**
 * Heat shading for the age matrix.
 *
 * Intensity rises with the count and the hue with the severity of the band, so
 * the eye lands on the worst cell without reading a single number. Zero is
 * deliberately dimmed rather than shaded: an empty cell is not a mild problem.
 */
export function heatStyle(count: number, band: '0-7' | '8-30' | '31-90' | '90+'): React.CSSProperties {
  if (!count) return { color: '#4b5563' };
  const rgb = band === '90+' ? '239,68,68' : band === '31-90' ? '245,158,11' : '37,99,235';
  const alpha = Math.min(0.28, 0.05 + count / 260);
  return { background: `rgba(${rgb},${alpha.toFixed(2)})`, color: '#e5e7eb' };
}

export const AgeBucket: React.FC<{ label: string; value: number; colour: string }> = ({ label, value, colour }) => (
  <div className="rounded-[0.625rem] border border-gray-700/70 p-3">
    <div className="text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-gray-400">{label}</div>
    <div className="text-[1.375rem] font-bold leading-tight" style={{ color: colour }}>{num(value)}</div>
  </div>
);

/** A blue call-out for the one sentence a reader should take away. */
export const Insight: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mt-3.5 rounded-lg border border-blue-500/30 bg-blue-600/10 px-3.5 py-3 text-[0.8125rem] leading-relaxed text-blue-100">
    {children}
  </p>
);

/**
 * The quiet paragraph that states what the figures cannot do.
 *
 * Kept because the mockup keeps it: a single snapshot cannot support
 * stage-to-stage conversion, and saying so is what separates this from a tool
 * that estimates one and hopes nobody checks.
 */
export const Honesty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="rounded-r-lg border-l-2 border-gray-700 bg-gray-900/40 px-3.5 py-3 text-xs leading-relaxed text-gray-400">
    {children}
  </p>
);
